import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { snakeCase } from 'lodash';
import { throwError as observableThrowError, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import CONFIGURATION from '../../configurations/configuration';

import { AuthService } from './auth.service';
import { LanguageService } from '../services/language.service';

@Injectable()
export class BaseService {
  customHeaderKeys: any;
  myInfoData: any;
  supportedLanguages: Array<string>;

  constructor(
    private authService: AuthService,
    private languageService: LanguageService,
    private http: HttpClient,
  ) {
    this.customHeaderKeys = {
      xHeaderLanguage: "x-header-language",
    };
    this.supportedLanguages = CONFIGURATION.supportedLanguages;
  }

  delete(url: string, customHeaders?: Array<string>): Observable<any> {
    return this.http.delete(url, this.generateRequestOptions(url, customHeaders)).pipe(map(this.mapResponse));
  }

  errorHandler(error: any) {
    let json;
    try {
      json = error.error;
    } catch (e) {
      return observableThrowError(error);
    }
    return observableThrowError(json);
  }

  generateRequestOptions(url?: string, customHeaders?: Array<string>) {
    let httpHeaders: HttpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.append('Accept', 'application/json');
    httpHeaders = httpHeaders.append('Content-Type', 'application/json');

    const omitHeader = url.indexOf('/p/') >= 0;
    if (!omitHeader) {
      const bearerToken = this.authService.getBearerToken();
      if (bearerToken) {
        httpHeaders = !httpHeaders.has("Authorization")
          ? httpHeaders.append('Authorization', 'Bearer ' + bearerToken)
          : httpHeaders.set('Authorization', 'Bearer ' + bearerToken);
      }

      const defaultLanguage = this.languageService.getDefaultLanguage();
      if (this.supportedLanguages.includes(defaultLanguage)) {
        httpHeaders = !httpHeaders.has('accept-language')
          ? httpHeaders.append('accept-language', `${defaultLanguage};q=0.9,en-US,en;q=0.8,zh-TW;q=0.6`)
          : httpHeaders.set('accept-language', `${defaultLanguage};q=0.9,en-US,en;q=0.8,zh-TW;q=0.6`);
        
        if ((customHeaders || []).includes(this.customHeaderKeys.xHeaderLanguage)) {
          httpHeaders = !httpHeaders.has(this.customHeaderKeys.xHeaderLanguage)
            ? httpHeaders.append(this.customHeaderKeys.xHeaderLanguage, defaultLanguage)
            : httpHeaders.set(this.customHeaderKeys.xHeaderLanguage, defaultLanguage);
        }
      }
    }

    return {
      headers: httpHeaders,
      observe: 'response' as 'body'
    };
  }

  get(url: string, customHeaders?: Array<string>): Observable<any> {
    return this.http.get(url, this.generateRequestOptions(url, customHeaders)).pipe(map(this.mapResponse));
  }

  getCustomXHeaderLanguageKey(): string {
    return this.customHeaderKeys.xHeaderLanguage;
  }

  getUrlWithParams(url: string, httpParams: HttpParams): string {
    let urlWithParams = url;
    if (urlWithParams && httpParams) {
      if (httpParams.keys().length > 0) {
        urlWithParams = `${urlWithParams}?${httpParams.toString()}`;
      }
      return urlWithParams;
    }
    return null;
  }

  mapResponse(response: any): any {
    let result: any;
    try {
      result = response.body;
    } catch (e) {
      throw new Error(response.toString());
    }
    return result;
  }

  post(url: string, body: any, customHeaders?: Array<string>): Observable<any> {
    return this.http.post(url, body, this.generateRequestOptions(url, customHeaders)).pipe(map(this.mapResponse));
  }

  put(url: string, body: any, customHeaders?: Array<string>): Observable<any> {
    return this.http.put(url, body, this.generateRequestOptions(url, customHeaders)).pipe(map(this.mapResponse));
  }

  toSnakeCaseRequestBody(body: object): object {
    let requestBody = {};
    const keys = Object.keys(body);
    if (keys.length > 0) {
      keys.forEach(key => {
        requestBody[snakeCase(key)] = body[key]
      });
    }
    return requestBody;
  }
}
